// src/v3/ui/symbol/symbol-detail.tsx

import { isRecord } from "@signals/shared";
import {
  Calculator,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  RefreshCw,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Markdown,
  qk,
  SYMBOL_AI_ANALYSIS_SECTION_OBJ,
  useEvents,
} from "../_common";
import { formatCell } from "../_common";
import { t } from "../locales";
import {
  useAnalyzeSymbol,
  useProcessSymbols,
  useRecalculateByPrice,
  useSymbolAiAnalyses,
  useSymbolAnalyses,
  useSymbolHistoryDigest,
  type AiSectionKey,
  type AnalysisHistoryEntry,
  type SymbolItem,
} from "./symbol.hooks";

const SYMBOL_VALUE_SCALE = {
  billion: 1_000_000_000,
  million: 1_000_000,
  thousand: 1_000,
} as const;
const COPY_FEEDBACK_RESET_DELAY_MS = 2_000;

const eventTypeToSection: Record<string, AiSectionKey> = {
  "analyze-summary": "summary",
  "analyze-reports": "reports",
  "analyze-history": "history",
};

function groupByDate(
  items: AnalysisHistoryEntry[],
): Map<string, AnalysisHistoryEntry[]> {
  const groups = new Map<string, AnalysisHistoryEntry[]>();
  for (const item of items) {
    const dateKey = item.date ? new Date(item.date).toLocaleDateString() : "—";
    const existing = groups.get(dateKey) ?? [];
    existing.push(item);
    groups.set(dateKey, existing);
  }
  return groups;
}

interface SymbolDetailProps {
  row: SymbolItem;
  onSymbolChanged?: () => void;
}

interface TierGroupProps {
  title: string;
  data: Record<string, unknown> | null | undefined;
  fields: Array<{
    key: string;
    label: string;
    format?: (v: unknown) => React.ReactNode;
  }>;
}

function formatNumber(v: unknown): string {
  if (typeof v === "number") {
    if (Math.abs(v) >= SYMBOL_VALUE_SCALE.billion)
      return `$${(v / SYMBOL_VALUE_SCALE.billion).toFixed(2)}B`;
    if (Math.abs(v) >= SYMBOL_VALUE_SCALE.million)
      return `$${(v / SYMBOL_VALUE_SCALE.million).toFixed(2)}M`;
    if (Math.abs(v) >= SYMBOL_VALUE_SCALE.thousand)
      return `$${(v / SYMBOL_VALUE_SCALE.thousand).toFixed(2)}K`;
    return v.toFixed(2);
  }
  return String(v ?? "—");
}

function formatPercent(v: unknown): string {
  if (typeof v === "number") return `${(v * 100).toFixed(2)}%`;
  return String(v ?? "—");
}

function formatBool(v: unknown): React.ReactNode {
  return formatCell(v, "boolean");
}


function getNestedValue(
  obj: Record<string, unknown> | null | undefined,
  path: string,
): unknown {
  if (!obj) return undefined;
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (isRecord(current) && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }
  return current;
}

function TierGroup({ title, data, fields }: TierGroupProps) {
  const [open, setOpen] = useState(false);
  const hasData =
    data &&
    Object.keys(data).length > 0 &&
    fields.some((f) => getNestedValue(data, f.key) !== undefined);

  return (
    <div className="rounded-md border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium hover:bg-muted"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        {title}
      </button>
      {open && (
        <div className="border-t p-3">
          {hasData ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {fields.map((f) => {
                const raw = getNestedValue(data, f.key);
                const value =
                  raw !== undefined && raw !== null
                    ? f.format
                      ? f.format(raw)
                      : String(raw)
                    : "—";
                return (
                  <div key={f.key}>
                    <span className="text-xs text-muted-foreground">
                      {f.label}
                    </span>
                    <div className="text-sm font-medium">{value}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-2 text-center text-sm text-muted-foreground">
              No financials data available for this tier
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface AiSectionProps {
  title: string;
  content: string | null;
  loading: boolean;
  running: boolean;
  progress: number;
  progressMsg: string | null;
  onRefresh: () => void;
  defaultOpen?: boolean;
}

function AiSection({
  title,
  content,
  loading,
  running,
  progress,
  progressMsg,
  onRefresh,
  defaultOpen = false,
}: AiSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const hasContent = content !== null && content !== "";

  const handleCopy = useCallback(async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_RESET_DELAY_MS);
    } catch {
      // clipboard not available
    }
  }, [content]);

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2 p-3">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-left text-sm font-medium hover:bg-muted flex-1"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          {title}
          {loading ? (
            <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-muted-foreground/40" />
          ) : hasContent ? (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-green-500"
              title="Analysis available"
            />
          ) : (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-red-400"
              title="No analysis yet"
            />
          )}
        </button>
        {hasContent && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-7 px-2"
            aria-label="Copy analysis text"
          >
            {copied ? (
              <span className="text-xs text-green-600">Copied!</span>
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={running}
          className="h-7 px-2"
        >
          {running ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      {open && (
        <div className="border-t p-3">
          {(running || (progress > 0 && progress < 100)) && (
            <div className="mb-3 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {running && <Loader2 className="h-3 w-3 animate-spin" />}
                <span className="flex-1 truncate">
                  {progressMsg ?? "Running..."}
                </span>
                {progress > 0 && progress < 100 && (
                  <span className="font-medium">{progress}%</span>
                )}
              </div>
              {progress > 0 && progress < 100 && (
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : hasContent ? (
            <div className="prose prose-sm max-w-none">
              <Markdown>{content}</Markdown>
            </div>
          ) : (
            <div className="py-2 text-center text-sm text-muted-foreground">
              No data yet. Click refresh to generate.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SymbolDetail({ row, onSymbolChanged }: SymbolDetailProps) {
  const queryClient = useQueryClient();
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(
    null,
  );
  const [runningSection, setRunningSection] = useState<AiSectionKey | null>(
    null,
  );
  const [sectionProgress, setSectionProgress] = useState<
    Record<AiSectionKey, { progress: number; msg: string | null }>
  >({
    summary: { progress: 0, msg: null },
    reports: { progress: 0, msg: null },
    history: { progress: 0, msg: null },
  });
  const [processLoading, setProcessLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { events, clearEvents } = useEvents([
    "analyze-summary",
    "analyze-reports",
    "analyze-history",
  ]);

  // ── Server-state: TanStack Query ────────────────────────────
  const analysesQuery = useSymbolAnalyses(row.symbol);
  const aiData = analysesQuery.data ?? null;
  const aiLoading = analysesQuery.isLoading;

  const historyDigestQuery = useSymbolHistoryDigest(row.symbol);
  const historyDigest = historyDigestQuery.data ?? null;
  const historyDigestLoading = historyDigestQuery.isLoading;

  const aiAnalysesQuery = useSymbolAiAnalyses(row.symbol);
  const history = aiAnalysesQuery.data ?? [];
  const historyLoaded = aiAnalysesQuery.isFetched;

  const analyzeMutation = useAnalyzeSymbol();
  const processMutation = useProcessSymbols();
  const recalculateMutation = useRecalculateByPrice();

  // SSE events drive progress updates and completion.
  // When progress reaches 100, we invalidate queries to reload data and
  // reset runningSection. This works even if the HTTP response hasn't
  // returned yet — SSE fires as soon as the route emits the final event.
  useEffect(() => {
    if (events.length === 0) return;
    const last = events[events.length - 1];
    if (!last) return;
    const section = eventTypeToSection[last.type];
    if (!section) return;

    setSectionProgress((prev) => ({
      ...prev,
      [section]: { progress: last.progress, msg: last.message ?? null },
    }));

    // When the workflow completes (100%), invalidate queries to reload
    // and stop the spinner.
    if (last.progress >= 100) {
      void queryClient.invalidateQueries({
        queryKey: qk.symbols.analyses(row.symbol),
      });
      if (section === SYMBOL_AI_ANALYSIS_SECTION_OBJ.HISTORY) {
        void queryClient.invalidateQueries({
          queryKey: qk.symbols.historyDigest(row.symbol),
        });
        void queryClient.invalidateQueries({
          queryKey: qk.symbols.aiAnalyses(row.symbol),
        });
      }
      setRunningSection(null);
    }
  }, [events, queryClient, row.symbol]);

  // Reset view state when symbol changes.
  // Query data resets automatically — query keys include row.symbol.
  useEffect(() => {
    setExpandedHistoryId(null);
    setRunningSection(null);
    setSectionProgress({
      summary: { progress: 0, msg: null },
      reports: { progress: 0, msg: null },
      history: { progress: 0, msg: null },
    });
  }, [row.id]);

  const runSection = useCallback(
    async (section: AiSectionKey) => {
      // Clear old SSE events so we don't pick up stale completion events
      clearEvents();
      setRunningSection(section);
      setSectionProgress((prev) => ({
        ...prev,
        [section]: { progress: 0, msg: "Starting..." },
      }));
      try {
        // AI workflows take 60-120+ seconds (multiple LLM tool calls).
        // The mutation uses a 5-minute timeout — SSE events keep the UI
        // updated in the meantime, and completion is driven by SSE
        // progress=100, not by the HTTP response.
        await analyzeMutation.mutateAsync({
          symbol: row.symbol,
          section,
          summary: section === SYMBOL_AI_ANALYSIS_SECTION_OBJ.REPORTS ? aiData?.summary : undefined,
        });
        // If the HTTP response arrives (not timed out), invalidate queries
        // to reload. SSE may have already done this, but it's harmless.
        void queryClient.invalidateQueries({
          queryKey: qk.symbols.analyses(row.symbol),
        });
        if (section === SYMBOL_AI_ANALYSIS_SECTION_OBJ.HISTORY) {
          void queryClient.invalidateQueries({
            queryKey: qk.symbols.historyDigest(row.symbol),
          });
          void queryClient.invalidateQueries({
            queryKey: qk.symbols.aiAnalyses(row.symbol),
          });
        }
        setRunningSection(null);
      } catch {
        // Don't reset runningSection on error — the backend may still be
        // processing. SSE progress=100 will reset it when the workflow
        // completes. If SSE never fires, the user can manually refresh.
        setSectionProgress((prev) => ({
          ...prev,
          [section]: {
            progress: prev[section].progress,
            msg: "Request timed out — backend may still be processing. Waiting for SSE completion...",
          },
        }));
      }
    },
    [row.symbol, aiData?.summary, analyzeMutation, queryClient, clearEvents],
  );

  const runAll = useCallback(async () => {
  const sections: AiSectionKey[] = [
    SYMBOL_AI_ANALYSIS_SECTION_OBJ.SUMMARY,
    SYMBOL_AI_ANALYSIS_SECTION_OBJ.REPORTS,
    SYMBOL_AI_ANALYSIS_SECTION_OBJ.HISTORY,
  ];
    for (const section of sections) {
      await runSection(section);
    }
  }, [runSection]);

  const handleProcess = useCallback(async () => {
    setProcessLoading(true);
    setActionError(null);
    try {
      await processMutation.mutateAsync({ symbols: [row.symbol] });
      onSymbolChanged?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Process failed");
    } finally {
      setProcessLoading(false);
    }
  }, [row.symbol, onSymbolChanged, processMutation]);

  const handleRecalculate = useCallback(async () => {
    if (row.price === null || row.price === undefined || row.price === 0) return;
    setRecalcLoading(true);
    setActionError(null);
    try {
      await recalculateMutation.mutateAsync([
        { symbol: row.symbol, price: row.price },
      ]);
      onSymbolChanged?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Recalculate failed");
    } finally {
      setRecalcLoading(false);
    }
  }, [row.symbol, row.price, onSymbolChanged, recalculateMutation]);

  const hasPrice = row.price !== null && row.price !== undefined && row.price !== 0;

  const groupedHistory = useMemo(() => groupByDate(history), [history]);

  const industryName = row.relations?.industry?.name ?? "—";
  const countryName = row.relations?.country?.name ?? "—";
  const financials = isRecord(row.financials) ? row.financials : null;

  const isRunning = runningSection !== null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            {row.symbol} — {row.name ?? row.symbol}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleProcess}
              disabled={processLoading || isRunning}
            >
              {processLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("symbol.action.process")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              disabled={recalcLoading || isRunning || !hasPrice}
              title={!hasPrice ? t("symbol.recalculateNoPrice") : undefined}
            >
              {recalcLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Calculator className="mr-2 h-4 w-4" />
              )}
              {t("symbol.action.recalculate")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runAll}
              disabled={isRunning}
            >
              {isRunning ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Zap className="mr-2 h-4 w-4" />
              )}
              Run All AI
            </Button>
          </div>
        </div>
      </CardHeader>
      {actionError && (
        <div className="border-t px-4 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <span className="text-xs text-muted-foreground">Exchange</span>
            <div className="text-sm font-medium">{row.exchange ?? "—"}</div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Industry</span>
            <div className="text-sm font-medium">{industryName}</div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Country</span>
            <div className="text-sm font-medium">{countryName}</div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Price</span>
            <div className="text-sm font-medium">
              {row.price !== null ? `$${row.price}` : "—"}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Market Cap</span>
            <div className="text-sm font-medium">
              {row.marketCap !== null
                ? `$${row.marketCap.toLocaleString()}`
                : "—"}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Volume</span>
            <div className="text-sm font-medium">
              {row.volume !== null ? row.volume.toLocaleString() : "—"}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">
              Статус: Система
            </span>
            <div className="text-sm font-medium">{row.autoStatus}</div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Статус: Мой</span>
            <div className="text-sm font-medium">{row.manualStatus ?? "—"}</div>
          </div>
        </div>

        <div className="rounded-md border p-3 space-y-2">
          <h4 className="text-sm font-medium">Flags</h4>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                key: "flags.isInterestingPrice",
                label: "Interesting Price",
                format: formatBool,
              },
              {
                key: "flags.isNoInterestStatus",
                label: "No Interest",
                format: formatBool,
              },
              {
                key: "flags.isInterestingPriceBroadStatus",
                label: "Interesting Price (Broad)",
                format: formatBool,
              },
              {
                key: "flags.isExpensiveStatus",
                label: "Expensive",
                format: formatBool,
              },
              {
                key: "flags.isVeryExpensiveStatus",
                label: "Very Expensive",
                format: formatBool,
              },
            ].map((field) => {
              const raw = getNestedValue(financials, field.key);
              const value =
                raw !== undefined && raw !== null
                  ? field.format
                    ? field.format(raw)
                    : String(raw)
                  : "—";
              return (
                <div key={field.key}>
                  <span className="text-xs text-muted-foreground">
                    {field.label}
                  </span>
                  <div className="text-sm font-medium">{value}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Left column: Financials */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Financials (4-tier)</h4>
            {financials ? (
              <>
                <TierGroup
                  title="Tier 1 — Balance / Income / Cash Flow"
                  data={financials}
                  fields={[
                    {
                      key: "balance.totalEquity.sum",
                      label: "Total Equity",
                      format: formatNumber,
                    },
                    {
                      key: "balance.netDebt.sum",
                      label: "Net Debt",
                      format: formatNumber,
                    },
                    {
                      key: "balance.debtToEquity.avg",
                      label: "Debt/Equity",
                      format: formatNumber,
                    },
                    {
                      key: "incomeStatement.revenue.sum",
                      label: "Revenue",
                      format: formatNumber,
                    },
                    {
                      key: "incomeStatement.operatingIncome.sum",
                      label: "Operating Income",
                      format: formatNumber,
                    },
                    {
                      key: "incomeStatement.netIncome.sum",
                      label: "Net Income",
                      format: formatNumber,
                    },
                    {
                      key: "incomeStatement.eps.sum",
                      label: "EPS",
                      format: formatNumber,
                    },
                    {
                      key: "incomeStatement.operationalMargin.avg",
                      label: "Op Margin",
                      format: formatPercent,
                    },
                    {
                      key: "incomeStatement.netMargin.avg",
                      label: "Net Margin",
                      format: formatPercent,
                    },
                    {
                      key: "cashFlow.freeCashFlow.sum",
                      label: "Free Cash Flow",
                      format: formatNumber,
                    },
                    {
                      key: "cashFlow.FCFPerShare.avg",
                      label: "FCF/Share",
                      format: formatNumber,
                    },
                  ]}
                />

                <TierGroup
                  title="Tier 2 — Coefficients / Growth Rates"
                  data={financials}
                  fields={[
                    {
                      key: "coefs.ROE.avg",
                      label: "ROE",
                      format: formatPercent,
                    },
                    {
                      key: "coefs.netROE.avg",
                      label: "Net ROE",
                      format: formatPercent,
                    },
                    {
                      key: "coefs.ROFCFE.avg",
                      label: "ROFCFE",
                      format: formatPercent,
                    },
                    {
                      key: "coefs.growthRates.operatingIncome.avg",
                      label: "Op Income Growth",
                      format: formatPercent,
                    },
                    {
                      key: "coefs.growthRates.netIncome.avg",
                      label: "Net Income Growth",
                      format: formatPercent,
                    },
                    {
                      key: "ratios.priceToEarningsRatioTTM",
                      label: "P/E TTM",
                      format: formatNumber,
                    },
                    {
                      key: "ratios.priceToBookRatioTTM",
                      label: "P/B TTM",
                      format: formatNumber,
                    },
                  ]}
                />

                <TierGroup
                  title="Tier 3 — Deal Income / Fair Prices"
                  data={financials}
                  fields={[
                    {
                      key: "dealIncome.perNetIncome",
                      label: "Per Net Income",
                      format: formatNumber,
                    },
                    {
                      key: "dealIncome.perFcf",
                      label: "Per FCF",
                      format: formatNumber,
                    },
                    {
                      key: "dealIncome.perFcfAndNetIncome",
                      label: "Per FCF+NI",
                      format: formatNumber,
                    },
                    {
                      key: "prices.fair.broad.min",
                      label: "Fair Broad Min",
                      format: formatNumber,
                    },
                    {
                      key: "prices.fair.broad.max",
                      label: "Fair Broad Max",
                      format: formatNumber,
                    },
                    {
                      key: "prices.fair.narrow.min",
                      label: "Fair Narrow Min",
                      format: formatNumber,
                    },
                    {
                      key: "prices.fair.narrow.max",
                      label: "Fair Narrow Max",
                      format: formatNumber,
                    },
                    {
                      key: "prices.buy.broad.min",
                      label: "Buy Broad Min",
                      format: formatNumber,
                    },
                    {
                      key: "prices.buy.broad.max",
                      label: "Buy Broad Max",
                      format: formatNumber,
                    },
                    {
                      key: "prices.sell.broad.min",
                      label: "Sell Broad Min",
                      format: formatNumber,
                    },
                    {
                      key: "prices.sell.broad.max",
                      label: "Sell Broad Max",
                      format: formatNumber,
                    },
                    {
                      key: "values.discountRate",
                      label: "Discount Rate",
                      format: formatPercent,
                    },
                    {
                      key: "values.marginOfSafety",
                      label: "Margin of Safety",
                      format: formatPercent,
                    },
                  ]}
                />


              </>
            ) : (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No financials data available
              </div>
            )}
          </div>

          {/* Right column: AI sections */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">AI Analysis</h4>
            <AiSection
              title="Company Summary"
              content={aiData?.summary ?? null}
              loading={aiLoading}
          running={runningSection === SYMBOL_AI_ANALYSIS_SECTION_OBJ.SUMMARY}
              progress={sectionProgress.summary.progress}
              progressMsg={sectionProgress.summary.msg}
              onRefresh={() => void runSection("summary")}
            />
            <AiSection
              title="Reports Analysis"
              content={aiData?.reports ?? null}
              loading={aiLoading}
          running={runningSection === SYMBOL_AI_ANALYSIS_SECTION_OBJ.REPORTS}
              progress={sectionProgress.reports.progress}
              progressMsg={sectionProgress.reports.msg}
              onRefresh={() => void runSection("reports")}
            />
            <AiSection
              title="Symbol History"
              content={historyDigest}
              loading={historyDigestLoading}
          running={runningSection === SYMBOL_AI_ANALYSIS_SECTION_OBJ.HISTORY}
              progress={sectionProgress.history.progress}
              progressMsg={sectionProgress.history.msg}
              onRefresh={() => void runSection("history")}
            />
          </div>
        </div>

        {historyLoaded && history.length > 0 && (
          <div className="mt-4 space-y-3">
            <h4 className="text-sm font-medium">Analysis History</h4>
            {Array.from(groupedHistory.entries()).map(
              ([date, entries]: [string, AnalysisHistoryEntry[]]) => (
                <div key={date} className="rounded-md border">
                  <div className="border-b bg-muted px-3 py-1.5 text-xs font-medium">
                    {date} ({entries.length})
                  </div>
                  <div className="space-y-1 p-3">
                    {entries.map((entry) => {
                      const isExpanded = expandedHistoryId === entry.id;
                      return (
                        <div key={entry.id} className="rounded border">
                          <button
                            onClick={() =>
                              setExpandedHistoryId(isExpanded ? null : entry.id)
                            }
                            className="flex w-full items-center gap-2 p-2 text-left text-sm hover:bg-muted"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <Badge>{entry.agent}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {entry.verdict}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {(entry.confidence * 100).toFixed(0)}%
                            </span>
                          </button>
                          {isExpanded && entry.reasoning && (
                            <div className="border-t p-3">
                              <div className="prose prose-sm max-w-none">
                                <Markdown>{entry.reasoning}</Markdown>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
